import { useEffect, useState, useCallback } from 'react'
import {
  Brain, RefreshCw, TrendingUp, TrendingDown, AlertTriangle,
  Zap, Target, Shield, BarChart3, Users, Lightbulb,
  ChevronRight, ArrowUpDown, Activity,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts'
import {
  getIntelligenceAccuracy,
  getIntelligenceSignals,
  getIntelligencePatterns,
  getIntelligenceMeta,
  getIntelligenceBrainStatus,
  getAgentSignals,
} from '@/lib/api'
import PageHeader from '@/components/ui/PageHeader'
import PageLoader from '@/components/ui/PageLoader'
import EmptyState from '@/components/ui/EmptyState'
import StatCard, { StatGrid } from '@/components/ui/StatCard'

/* ── helpers ─────────────────────────────────────────────────────────────── */

function strengthBadge(s: string) {
  if (s === 'strong') return 'bg-emerald/15 text-emerald border border-emerald/30'
  if (s === 'moderate') return 'bg-amber/15 text-amber border border-amber/30'
  return 'bg-slate-500/15 text-text-muted border border-border'
}

function pct(n: number) { return `${n.toFixed(1)}%` }

const AGENT_COLORS: Record<string, string> = {
  aria: '#a78bfa',
  portfolio: '#3b82f6',
  risk: '#f43f5e',
  market: '#22c55e',
  compliance: '#f59e0b',
  goals: '#06b6d4',
}

/* ── section wrapper ─────────────────────────────────────────────────────── */

function Section({ icon: Icon, title, badge, children }: {
  icon: React.ElementType; title: string; badge?: string | number; children: React.ReactNode
}) {
  return (
    <section className="card overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border">
        <Icon size={16} className="text-blue-light shrink-0" />
        <h2 className="panel-title">{title}</h2>
        {badge !== undefined && (
          <span className="ml-auto text-xs font-mono bg-blue/10 text-blue-light px-2 py-0.5 rounded-full">
            {badge}
          </span>
        )}
      </div>
      <div className="p-5">{children}</div>
    </section>
  )
}

/* ── main page ───────────────────────────────────────────────────────────── */

export default function BrainPage() {
  const [loading, setLoading] = useState(true)
  const [accuracy, setAccuracy] = useState<any>(null)
  const [signals, setSignals] = useState<any>(null)
  const [patterns, setPatterns] = useState<any>(null)
  const [meta, setMeta] = useState<any>(null)
  const [brainStatus, setBrainStatus] = useState<any>(null)
  const [agentSignals, setAgentSignals] = useState<any>(null)
  const [signalsLoading, setSignalsLoading] = useState(false)
  const [signalTicker, setSignalTicker] = useState('')

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [acc, sig, pat, met, status] = await Promise.all([
        getIntelligenceAccuracy(),
        getIntelligenceSignals(),
        getIntelligencePatterns(),
        getIntelligenceMeta(),
        getIntelligenceBrainStatus().catch(() => null),
      ])
      setAccuracy(acc)
      setSignals(sig)
      setPatterns(pat)
      setMeta(met)
      setBrainStatus(status)
    } catch (e) {
      console.error('Intelligence fetch failed:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchSignals = useCallback(async (ticker?: string) => {
    setSignalsLoading(true)
    try {
      const data = await getAgentSignals(ticker || undefined)
      setAgentSignals(data)
    } catch (e) {
      console.error('Agent signals fetch failed:', e)
    } finally {
      setSignalsLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  if (loading) {
    return <PageLoader />
  }

  const leaderboard = accuracy?.leaderboard || []
  const signalList = signals?.signals || []
  const metaData = meta || {}
  const insights = metaData.insights || []

  // Chart data for agent accuracy
  const chartData = leaderboard.map((a: any) => ({
    name: a.agent_id,
    accuracy: a.accuracy,
    total: a.total,
    fill: AGENT_COLORS[a.agent_id] || '#64748b',
  }))

  return (
    <div className="space-y-6 animate-fade-in max-w-screen-xl mx-auto">
      <PageHeader
        title="Agent Brain"
        icon={Brain}
        iconClassName="text-purple-400"
        subtitle="How your agents learn, what works, and what patterns are active right now."
        actions={
          <button onClick={fetchAll} className="btn-secondary gap-1.5 text-sm">
            <RefreshCw size={14} aria-hidden />
            Refresh
          </button>
        }
      />

      {/* Agent Signals section */}
      <section className="card overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border">
          <Activity size={16} className="text-blue-light shrink-0" />
          <h2 className="panel-title">Agent Signals</h2>
          <span className="text-xs text-text-muted ml-1">— bullish / bearish / neutral per persona</span>
          <div className="ml-auto flex items-center gap-2">
            <input
              type="text"
              value={signalTicker}
              onChange={e => setSignalTicker(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && fetchSignals(signalTicker || undefined)}
              placeholder="AAPL (blank = portfolio)"
              className="input w-44 text-xs px-2 py-1"
            />
            <button
              type="button"
              onClick={() => fetchSignals(signalTicker || undefined)}
              disabled={signalsLoading}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue/10 border border-blue/30
                         text-blue-light rounded-lg hover:bg-blue/20 transition-colors disabled:opacity-50"
            >
              {signalsLoading
                ? <RefreshCw size={12} className="animate-spin" />
                : <Zap size={12} />}
              {signalsLoading ? 'Running…' : 'Run Signals'}
            </button>
          </div>
        </div>
        <div className="p-5">
          {agentSignals ? (
            <AgentSignalsGrid data={agentSignals} />
          ) : (
            <div className="py-6 text-center">
              <Activity size={26} className="mx-auto mb-2 text-text-muted/40" />
              <p className="text-sm text-text-muted">
                Click <span className="text-blue-light font-medium">Run Signals</span> to gather bullish/bearish/neutral
                signals from all 6 persona agents across your portfolio holdings.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats row */}
      <StatGrid cols={4}>
        <StatCard
          label="Outcomes Tracked"
          value={metaData.total_outcomes || 0}
          icon={Target}
          size="compact"
        />
        <StatCard
          label="Overall Win Rate"
          value={pct(metaData.overall_win_rate || 0)}
          icon={TrendingUp}
          size="compact"
          valueClassName={(metaData.overall_win_rate || 0) > 50 ? 'text-emerald' : 'text-rose-light'}
        />
        <StatCard
          label="Active Patterns"
          value={patterns?.active_patterns || 0}
          icon={Zap}
          size="compact"
        />
        <StatCard
          label="Agents Tracked"
          value={leaderboard.length}
          icon={Users}
          size="compact"
        />
      </StatGrid>

      {/* Engine room — what the brain already knows from real stores */}
      {brainStatus && (
        <Section icon={Activity} title="Engine Room — live intelligence stores">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="py-3 px-4 rounded-lg bg-surface-DEFAULT/50 border border-border">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Signal store</p>
              <p className="text-lg font-semibold text-text-primary">
                {(brainStatus.signals_store?.rows_24h ?? 0).toLocaleString()}
                <span className="text-xs font-normal text-text-muted"> signals / 24h</span>
              </p>
              <p className="text-xs text-text-muted">
                from {brainStatus.signals_store?.distinct_sources_24h ?? 0} distinct sources
              </p>
            </div>
            <div className="py-3 px-4 rounded-lg bg-surface-DEFAULT/50 border border-border">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Prediction engine</p>
              <p className="text-lg font-semibold text-text-primary">
                {(brainStatus.predictions?.total ?? 0).toLocaleString()}
                <span className="text-xs font-normal text-text-muted"> forecasts recorded</span>
              </p>
              <p className="text-xs text-text-muted">
                {brainStatus.predictions?.latest_at
                  ? `latest ${new Date(brainStatus.predictions.latest_at).toLocaleString()}`
                  : `none yet — next emit ${brainStatus.predictions?.next_emit ?? 'per schedule'}`}
              </p>
            </div>
            <div className="py-3 px-4 rounded-lg bg-surface-DEFAULT/50 border border-border">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">Idea blotter funnel</p>
              {Object.keys(brainStatus.blotter_funnel ?? {}).length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(brainStatus.blotter_funnel).map(([status, n]: [string, any]) => (
                    <span key={status} className="text-xs px-2 py-0.5 rounded-full bg-blue/10 text-blue-light border border-blue/20">
                      {status}: {n}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-text-muted mt-1">
                  No ideas logged yet — the convergence scanners write here as
                  multi-source setups fire (every 30 min – 2 h).
                </p>
              )}
            </div>
          </div>

          {/* Empirical pattern grades */}
          <div className="mt-4">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
              Empirical pattern grades
              {brainStatus.pattern_grades?.as_of && (
                <span className="ml-2 normal-case font-normal">as of {brainStatus.pattern_grades.as_of}</span>
              )}
            </p>
            {(brainStatus.pattern_grades?.grades ?? []).length > 0 ? (
              <div className="space-y-1.5">
                {(brainStatus.pattern_grades.grades as any[]).slice(0, 8).map(g => (
                  <div key={g.pattern} className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-surface-DEFAULT/50 text-sm">
                    <span className="text-text-primary">{String(g.pattern).replace(/_/g, ' ')}</span>
                    <span className="text-xs text-text-muted">
                      n={g.n ?? 0}
                      <span className={`ml-3 font-semibold ${(g.win_rate_pct ?? 0) >= 55 ? 'text-emerald' : (g.win_rate_pct ?? 0) >= 45 ? 'text-amber' : 'text-rose-light'}`}>
                        {g.win_rate_pct != null ? `${g.win_rate_pct}%` : '—'}
                      </span>
                      <span className="ml-3">{g.avg_return_pct != null ? `${g.avg_return_pct > 0 ? '+' : ''}${g.avg_return_pct}% avg` : ''}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-text-muted">
                Not computed yet — the pattern backtest grades every technical
                pattern against 90 days of forward returns and runs{' '}
                {brainStatus.pattern_grades?.next_run ?? 'weekly'}. Grades appear
                here after its first run.
              </p>
            )}
          </div>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 stagger-children">
        {/* Agent Accuracy Leaderboard */}
        <Section icon={BarChart3} title="Agent Accuracy Leaderboard" badge={`${leaderboard.length} agents`}>
          {leaderboard.length > 0 ? (
            <div className="space-y-4">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} width={70} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgb(var(--bg-surface))',
                        border: '1px solid #334155',
                        borderRadius: 8,
                        fontSize: 12, color: 'rgb(var(--text-primary))'}}
                      formatter={(v: number) => [`${v}%`, 'Accuracy']}
                    />
                    <Bar dataKey="accuracy" radius={[0, 4, 4, 0]} barSize={20}>
                      {chartData.map((entry: any, i: number) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5">
                {leaderboard.map((a: any) => (
                  <div
                    key={a.agent_id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-surface-DEFAULT/50"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: AGENT_COLORS[a.agent_id] || '#64748b' }}
                      />
                      <span className="text-sm font-medium text-text-primary capitalize">{a.agent_id}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-text-muted">
                      <span>{a.correct}/{a.total} correct</span>
                      <span className={`font-semibold ${a.accuracy >= 55 ? 'text-emerald' : a.accuracy >= 45 ? 'text-amber' : 'text-rose-light'}`}>
                        {pct(a.accuracy)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              icon={Brain}
              title="Nothing graded yet"
              description={
                brainStatus?.grading
                  ? `${brainStatus.grading.how_it_fills ?? ''} Grading runs ${brainStatus.grading.next_run ?? 'nightly'}; ` +
                    `${brainStatus.grading.recommendations_total ?? 0} recommendation(s) recorded so far, ` +
                    `${brainStatus.grading.recommendations_evaluated ?? 0} graded.`
                  : 'Approve recommendations and record trade results to start tracking agent accuracy.'
              }
            />
          )}
        </Section>

        {/* Best Performing Signals */}
        <Section icon={Zap} title="Best Performing Signals" badge={`${signalList.length} signals`}>
          {signalList.length > 0 ? (
            <div className="space-y-2">
              {signalList.map((s: any, i: number) => (
                <div
                  key={s.signal_type}
                  className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-surface-DEFAULT/50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-text-muted w-5">#{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-text-primary">{s.signal_type.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-text-muted">{s.wins}W / {s.losses}L ({s.total} total)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-surface-DEFAULT rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${s.win_rate}%`,
                          backgroundColor: s.win_rate >= 60 ? '#34d399' : s.win_rate >= 45 ? '#f59e0b' : '#fb7185',
                        }}
                      />
                    </div>
                    <span className={`text-sm font-semibold ${s.win_rate >= 55 ? 'text-emerald' : s.win_rate >= 45 ? 'text-amber' : 'text-rose-light'}`}>
                      {pct(s.win_rate)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Zap}
              title="No signal outcomes yet"
              description={
                `Signal win rates appear once trade outcomes are recorded against signal labels — ` +
                `act on blotter ideas or record results on closed trades. ` +
                `Meanwhile the Engine Room above shows the empirical pattern grades already computed from the signal store` +
                (brainStatus?.signals_store?.rows_24h
                  ? ` (${brainStatus.signals_store.rows_24h.toLocaleString()} signals in the last 24h).`
                  : '.')
              }
            />
          )}
        </Section>

        {/* Currently Detected Patterns */}
        <Section icon={AlertTriangle} title="Active Patterns" badge={patterns?.active_patterns || 0}>
          <div className="space-y-3">
            {/* Smart Money Convergence */}
            {(patterns?.smart_money_convergence || []).length > 0 && (
              <PatternGroup title="Smart Money Convergence" icon={Users}>
                {patterns.smart_money_convergence.map((p: any) => (
                  <PatternCard
                    key={p.ticker}
                    title={p.ticker}
                    description={p.description}
                    strength={p.strength}
                    signals={p.signals}
                  />
                ))}
              </PatternGroup>
            )}

            {/* Sector Rotation */}
            {patterns?.sector_momentum_shift?.detected && (
              <PatternGroup title="Sector Momentum Shift" icon={ArrowUpDown}>
                <PatternCard
                  title="Sector Rotation"
                  description={patterns.sector_momentum_shift.description}
                  strength={patterns.sector_momentum_shift.strength}
                />
              </PatternGroup>
            )}

            {/* Risk Regime */}
            {patterns?.risk_regime_change?.detected && (
              <PatternGroup title="Risk Regime Change" icon={Shield}>
                <PatternCard
                  title={`Regime: ${patterns.risk_regime_change.current_regime}`}
                  description={patterns.risk_regime_change.description}
                  strength={patterns.risk_regime_change.strength}
                />
              </PatternGroup>
            )}

            {/* Contrarian */}
            {(patterns?.contrarian_opportunities || []).length > 0 && (
              <PatternGroup title="Contrarian Opportunities" icon={TrendingDown}>
                {patterns.contrarian_opportunities.map((p: any) => (
                  <PatternCard
                    key={p.ticker}
                    title={p.ticker}
                    description={p.description}
                    strength={p.strength}
                  />
                ))}
              </PatternGroup>
            )}

            {(patterns?.active_patterns || 0) === 0 && (
              <EmptyState
                icon={AlertTriangle}
                title="No active patterns detected"
                description="The system continuously scans alt data for smart money convergence, sector rotation, regime shifts, and contrarian setups."
              />
            )}
          </div>
        </Section>

        {/* Signal Confluence & Meta Insights */}
        <Section icon={Lightbulb} title="Meta Insights" badge={`${insights.length} insights`}>
          {insights.length > 0 ? (
            <div className="space-y-3">
              {insights.map((insight: string, i: number) => (
                <div
                  key={i}
                  className="flex items-start gap-3 py-2.5 px-3 rounded-lg bg-surface-DEFAULT/50"
                >
                  <ChevronRight size={14} className="text-blue-light mt-0.5 shrink-0" />
                  <p className="text-sm text-text-secondary">{insight}</p>
                </div>
              ))}

              {/* Consensus correlation */}
              {metaData.consensus_correlation && (
                <div className="mt-4 p-4 rounded-lg bg-blue/5 border border-blue/20">
                  <p className="text-xs font-semibold text-blue-light mb-2">Consensus vs Outcome</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-text-muted text-xs">High agreement (&ge;60%)</p>
                      <p className="text-emerald font-semibold">
                        {pct(metaData.consensus_correlation.high_agreement_win_rate)} win rate
                      </p>
                    </div>
                    <div>
                      <p className="text-text-muted text-xs">Low agreement (&lt;40%)</p>
                      <p className="text-rose-light font-semibold">
                        {pct(metaData.consensus_correlation.low_agreement_win_rate)} win rate
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-text-muted mt-2">{metaData.consensus_correlation.conclusion}</p>
                </div>
              )}

              {/* Regime performance */}
              {Object.keys(metaData.regime_performance || {}).length > 0 && (
                <div className="mt-4 p-4 rounded-lg bg-surface-DEFAULT/50 border border-border">
                  <p className="text-xs font-semibold text-text-primary mb-2">Performance by Market Regime</p>
                  <div className="space-y-1.5">
                    {Object.entries(metaData.regime_performance).map(([regime, stats]: [string, any]) => (
                      <div key={regime} className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary capitalize">{regime.replace(/_/g, ' ')}</span>
                        <span className={stats.win_rate >= 55 ? 'text-emerald' : stats.win_rate >= 45 ? 'text-amber' : 'text-rose-light'}>
                          {pct(stats.win_rate)} ({stats.total} trades)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState
              icon={Lightbulb}
              title="No meta insights yet"
              description={
                `Meta-analysis needs graded outcomes. ` +
                (brainStatus?.grading
                  ? `The grader runs ${brainStatus.grading.next_run ?? 'nightly'} over recommendations older than 7 days ` +
                    `(${brainStatus.grading.recommendations_total ?? 0} recorded, ${brainStatus.grading.recommendations_evaluated ?? 0} graded). ` +
                    `Cross-agent correlations appear as those grades accumulate.`
                  : 'The system will surface cross-agent patterns and correlations as data accumulates.')
              }
            />
          )}
        </Section>
      </div>
    </div>
  )
}

/* ── Sub-components ──────────────────────────────────────────────────────── */

function PatternGroup({ title, icon: Icon, children }: {
  title: string; icon: React.ElementType; children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} className="text-text-muted" />
        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{title}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function PatternCard({ title, description, strength, signals }: {
  title: string; description: string; strength: string; signals?: Record<string, boolean>
}) {
  return (
    <div className="py-3 px-4 rounded-lg bg-surface-DEFAULT/50 border border-border">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-text-primary">{title}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${strengthBadge(strength)}`}>
          {strength}
        </span>
      </div>
      <p className="text-xs text-text-secondary">{description}</p>
      {signals && (
        <div className="flex gap-2 mt-2">
          {Object.entries(signals).map(([key, active]) => (
            <span
              key={key}
              className={`text-xs px-1.5 py-0.5 rounded ${
                active ? 'bg-emerald/15 text-emerald' : 'bg-surface-DEFAULT text-text-muted'
              }`}
            >
              {key.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Agent Signals Grid ───────────────────────────────────────────────────── */

const PERSONA_COLS = [
  { id: 'buffett', short: 'WB', name: 'Warren Buffett',  tw: 'text-emerald-400' },
  { id: 'dalio',   short: 'RD', name: 'Ray Dalio',       tw: 'text-blue-400'    },
  { id: 'soros',   short: 'GS', name: 'George Soros',    tw: 'text-amber-400'   },
  { id: 'simons',  short: 'JS', name: 'Jim Simons',      tw: 'text-violet-400'  },
  { id: 'cathie',  short: 'CW', name: 'Cathie Wood',     tw: 'text-pink-400'    },
  { id: 'burry',   short: 'MB', name: 'Michael Burry',   tw: 'text-rose-400'    },
]

function signalClasses(signal: string) {
  if (signal === 'bullish')  return 'bg-emerald/15 text-emerald border border-emerald/30'
  if (signal === 'bearish')  return 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
  return 'bg-slate-500/15 text-text-muted border border-border'
}

function signalIcon(signal: string) {
  if (signal === 'bullish') return '▲'
  if (signal === 'bearish') return '▼'
  return '◆'
}

function AgentSignalsGrid({ data }: { data: any }) {
  const tickers: string[] = data?.tickers ?? []
  const signalsMap: Record<string, any[]> = data?.signals ?? {}
  const decisions: any[] = data?.decisions ?? []

  if (!tickers.length) {
    return <EmptyState icon={Activity} title="No tickers found" description="The signals run returned no tickers." />
  }

  return (
    <div className="space-y-5">
      {/* Ticker × Persona matrix */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs table-pro">
          <thead>
            <tr>
              <th className="w-20">Ticker</th>
              {PERSONA_COLS.map(p => (
                <th key={p.id} className="!text-center min-w-[72px]">
                  <span
                    className={`inline-block font-bold text-xs px-1.5 py-0.5 rounded ${p.tw}`}
                    title={p.name}
                  >
                    {p.short}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickers.map(ticker => {
              const sigs = signalsMap[ticker] ?? []
              const byAgent: Record<string, any> = {}
              for (const s of sigs) byAgent[s.agent_id] = s

              // Overall consensus for this ticker
              const bullCount = sigs.filter(s => s.signal === 'bullish').length
              const bearCount = sigs.filter(s => s.signal === 'bearish').length
              const majority = bullCount > bearCount ? 'bullish' : bearCount > bullCount ? 'bearish' : 'neutral'

              return (
                <tr key={ticker}>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-text-primary">{ticker}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${signalClasses(majority)}`}>
                        {signalIcon(majority)}
                      </span>
                    </div>
                  </td>
                  {PERSONA_COLS.map(p => {
                    const s = byAgent[p.id]
                    const signal = s?.signal ?? 'neutral'
                    const conf = s?.confidence ?? 0
                    return (
                      <td key={p.id} className="py-2.5 px-2 text-center">
                        <div
                          className={`inline-flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-xs font-medium cursor-default ${signalClasses(signal)}`}
                          title={s?.reasoning ?? ''}
                        >
                          <span>{signalIcon(signal)}</span>
                          <span className="font-mono text-[10px]">{conf > 0 ? `${Math.round(conf)}%` : '—'}</span>
                        </div>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-text-muted">
        <span className="flex items-center gap-1">
          <span className="text-emerald">▲</span> Bullish
        </span>
        <span className="flex items-center gap-1">
          <span className="text-rose-400">▼</span> Bearish
        </span>
        <span className="flex items-center gap-1">
          <span>◆</span> Neutral
        </span>
        <span className="ml-2">Hover cells for reasoning · Number = confidence %</span>
      </div>

      {/* PM Decisions table */}
      {decisions.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            Portfolio Manager Decisions
          </p>
          <div className="space-y-2">
            {decisions.map((d: any) => {
              const action = d.action ?? 'HOLD'
              const actionColor =
                action === 'BUY'  ? 'text-emerald bg-emerald/10 border-emerald/30' :
                action === 'SELL' ? 'text-rose-400 bg-rose-500/10 border-rose-500/30' :
                action === 'AVOID' ? 'text-amber bg-amber/10 border-amber/30' :
                'text-text-muted bg-surface-DEFAULT border-border'
              return (
                <div
                  key={d.ticker}
                  className="flex items-start gap-3 px-4 py-3 rounded-lg bg-surface-DEFAULT/50 border border-border"
                >
                  <span className="font-mono font-semibold text-sm text-text-primary w-14 shrink-0">{d.ticker}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded border shrink-0 ${actionColor}`}>
                    {action}
                  </span>
                  {d.suggested_position_pct > 0 && (
                    <span className="text-xs text-text-muted shrink-0">{d.suggested_position_pct?.toFixed(1)}%</span>
                  )}
                  <span className="text-xs text-text-secondary flex-1">{d.rationale}</span>
                  <div className="shrink-0 text-right">
                    <span className={`text-xs font-medium ${
                      d.bull_pct > 55 ? 'text-emerald' : d.bear_pct > 55 ? 'text-rose-400' : 'text-text-muted'
                    }`}>
                      {d.bull_pct?.toFixed(0)}% bull
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
